/*
 * Copyright (c) Nexign, JSC, 2022-2023.
 */
/**
 *
 * @author Ilya Babanov
 * @version 1.23.0
 * @since 1.22.0
 */

import { useEffect } from 'react';
import type { FC } from 'react';

import { Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';

import type { TClientId } from '@/main/pages/PageAccountInfo/types';
import type { IConnection } from '@/main/pages/PageAccountInfo/types/pageAccountInfoGeneralTypes';
import { makeSelectAuthoritiesIsServiceUser } from '@/main/pages/PageAuth/selectors/authSelectors';
import { ERequestStatus } from '@/main/types/globalTypes';
import { icons } from '@assets/index';
import { LinkBlock } from '@components/common';
import { ImgStyled } from '@components/common/Block/Img';
import ArrowTooltip from '@components/common/Tooltip/Tooltip';

import {
  makeSelectEdmOperatorConnectionTranslation,
  makeSelectTranslationCommon,
} from '../../internationalization/intSelectors';
import { Loader } from '../../loader/components/Loader';
import Modal from '../../modal/components/Modal';
import {
  ModalTitleStyled,
  ModalTitleTextStyled,
} from '../../modal/components/styled/ModalStyled';
import { modalSetStatusAction } from '../../modal/modalActions';
import { EModalStatus } from '../../modal/types/modalTypes';
import ButtonServiceUserDenied from '../../role/components/ButtonServiceUserDenied';
import { useUpdateEdmResponsibleContactMutation } from '../store';
import { EdmConnectionId, TEditEdmResponsibleForm } from '../types';
import {
  setEditButtonTooltip,
  setEditButtonTooltipHoverListener,
} from '../util/setTooltipTitle';
import {
  mapFormDataToUpdateEdmResponsibleRequest,
  setEditResponsibleInitialFormValues,
} from '../util/transformEdmDelivery';
import { setEditResponsibleValidationSchema } from '../util/validation';
import { EdmRequestStatusModal } from './EdmRequestStatusModal';
import { EdmResponsibleContact } from './EdmResponsibleContact';

interface IProps {
  clientId: TClientId;
  connection: IConnection;
  onCloseModal: () => void;
}

export const EdmResponsibleEditModal: FC<IProps> = ({
  clientId,
  connection,
  onCloseModal,
}) => {
  const dispatch = useDispatch();

  const tCommon = useSelector(makeSelectTranslationCommon());
  const { start: tEditStart, failure: tEditFailure } = useSelector(
    makeSelectEdmOperatorConnectionTranslation(),
  );
  const isServiceUser = useSelector(makeSelectAuthoritiesIsServiceUser());

  const { allowPersonNameEdit, allowPhoneNumberEdit, allowEmailEdit, id } =
    connection;

  const validationSchema = setEditResponsibleValidationSchema(
    connection,
    tCommon.form,
  );
  const initialValues = setEditResponsibleInitialFormValues(connection);

  const [updateResponsible, { error, isError, isLoading, isSuccess }] =
    useUpdateEdmResponsibleContactMutation();

  useEffect(() => {
    if (isSuccess) {
      onCloseModal();
    }
  }, [isSuccess, onCloseModal]);

  useEffect(() => {
    if (isError) {
      dispatch(
        modalSetStatusAction({
          status: EModalStatus.failure,
        }),
      );
    }
  }, [dispatch, isError]);

  const handleSubmit = (formData: TEditEdmResponsibleForm) => {
    const data = mapFormDataToUpdateEdmResponsibleRequest(formData);
    updateResponsible({
      bisAccountId: clientId,
      edmConnectionId: EdmConnectionId(id),
      data,
    });
  };

  return (
    <Modal
      dataName="edm-responsible-edit-modal"
      style={{ padding: '24px' }}
      width="480px"
    >
      <Modal.Status status={EModalStatus.start}>
        <ModalTitleStyled>
          <ModalTitleTextStyled className="title-text-icon">
            <span className="title-text">{tEditStart.responsibleContact}</span>
            <ArrowTooltip title={tEditStart.responsibleContactTooltip}>
              <ImgStyled height="20px" src={icons.infoBlueIcon} width="20px" />
            </ArrowTooltip>
          </ModalTitleTextStyled>
          <ImgStyled
            height="16px"
            src={icons.cancelSmallIcon}
            width="16px"
            onClick={onCloseModal}
          />
        </ModalTitleStyled>
        <Formik<TEditEdmResponsibleForm>
          enableReinitialize
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ dirty, isValid, submitForm }) => (
            <Loader showLoader={isLoading}>
              <Modal.Body style={{ maxHeight: 'initial', paddingTop: '16px' }}>
                <EdmResponsibleContact
                  allowEmailEdit={allowEmailEdit}
                  allowPersonNameEdit={allowPersonNameEdit}
                  allowPhoneNumberEdit={allowPhoneNumberEdit}
                />
              </Modal.Body>
              <Modal.Control className="modal-popup-control__base">
                <ArrowTooltip
                  disableHoverListener={setEditButtonTooltipHoverListener(
                    isServiceUser,
                    dirty,
                    isValid,
                  )}
                  title={setEditButtonTooltip(dirty, isServiceUser, tEditStart)}
                >
                  <span>
                    <ButtonServiceUserDenied
                      disabled={!dirty || !isValid || isServiceUser}
                      onClick={submitForm}
                    >
                      {tEditStart.editButton}
                    </ButtonServiceUserDenied>
                  </span>
                </ArrowTooltip>
                <LinkBlock onClick={onCloseModal}>
                  {tCommon.modal.cancel}
                </LinkBlock>
              </Modal.Control>
            </Loader>
          )}
        </Formik>
      </Modal.Status>
      <Modal.Status status={EModalStatus.failure}>
        <EdmRequestStatusModal
          mode={ERequestStatus.fail}
          requestError={error}
          title={tEditFailure.editResponsibleTitle}
        />
      </Modal.Status>
    </Modal>
  );
};
